import React, { useRef, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import './AccountDetailsDialog.css';

export interface LegalLitigationAccount {
  loan_number: string;
  customer_name: string;
  litigation_state: string;
  litigation_county: string;
  collection_officer: string;
  attorney_file_number: string;
  case_number: string;
  case_filing_date: string;
  service_date: string;
  judgement_date: string;
  garn_filing_date: string;
  garn_service_date: string;
  garn_answer_date: string;
  last_payment_date: string;
  last_payment_amount: number;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface SortingInfo {
  sort_by: string;
  sort_order: string;
}

interface LegalAttorneyAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: LegalLitigationAccount[];
  title: string;
  loading?: boolean;
  errorMessage?: string | null;
  pagination?: PaginationInfo;
  sorting?: SortingInfo;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  onSearchChange?: (searchLoanNumber: string, searchCustomerName: string) => void;
  searchLoanNumber?: string;
  searchCustomerName?: string;
  isSearchPending?: boolean;
  onSearchComplete?: () => void;
  onExportAllData?: () => Promise<LegalLitigationAccount[]>;
}

const COL_COUNT = 15;

const LegalAttorneyAccountsDialog: React.FC<LegalAttorneyAccountsDialogProps> = ({
  isOpen,
  onClose,
  accounts,
  title,
  loading = false,
  errorMessage = null,
  pagination,
  sorting,
  onPageChange,
  onSortChange,
  onSearchChange,
  searchLoanNumber = '',
  searchCustomerName = '',
  isSearchPending = false,
  onSearchComplete,
  onExportAllData,
}) => {
  const loanNumberInputRef = useRef<HTMLInputElement>(null);
  const customerNameInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedInputRef = useRef<'loan' | 'customer' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  useEffect(() => {
    if (!isSearchPending && lastFocusedInputRef.current && onSearchComplete) {
      setTimeout(() => {
        if (lastFocusedInputRef.current === 'loan' && loanNumberInputRef.current) {
          loanNumberInputRef.current.focus();
          const len = loanNumberInputRef.current.value.length;
          loanNumberInputRef.current.setSelectionRange(len, len);
        } else if (lastFocusedInputRef.current === 'customer' && customerNameInputRef.current) {
          customerNameInputRef.current.focus();
          const len = customerNameInputRef.current.value.length;
          customerNameInputRef.current.setSelectionRange(len, len);
        }
        onSearchComplete();
      }, 50);
    }
  }, [isSearchPending, onSearchComplete]);

  if (!isOpen) return null;

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const totalAccountsCount = pagination?.total_count ?? accounts.length;

  const handleSort = (column: string) => {
    if (!onSortChange) return;
    const currentSortBy = sorting?.sort_by || 'loan_number';
    const currentSortOrder = sorting?.sort_order || 'ASC';
    const newSortOrder =
      currentSortBy === column && currentSortOrder === 'DESC' ? 'ASC' : 'DESC';
    onSortChange(column, newSortOrder);
  };

  const getSortIcon = (column: string) => {
    if (!sorting || sorting.sort_by !== column) return '↕️';
    return sorting.sort_order === 'ASC' ? '↑' : '↓';
  };

  const exportToExcel = async () => {
    if (!onExportAllData) {
      alert('Export is not available');
      return;
    }
    if (isExporting) return;
    try {
      setIsExporting(true);
      setExportProgress('Fetching all rows…');
      const all = await onExportAllData();
      if (all.length === 0) {
        alert('No data to export');
        return;
      }
      const exportData = all.map((a) => ({
        'Loan Number': a.loan_number,
        'Customer Name': a.customer_name,
        'Litigation State': a.litigation_state,
        'Litigation County': a.litigation_county,
        'Collection Officer': a.collection_officer,
        'Attorney File Number': a.attorney_file_number,
        'Case Number': a.case_number,
        'Case Filing Date': a.case_filing_date,
        'Service Date': a.service_date,
        'Judgement Date': a.judgement_date,
        'Garn Filing Date': a.garn_filing_date,
        'Garn Service Date': a.garn_service_date,
        'Garn Answer Date (udf50)': a.garn_answer_date,
        'Last Payment Date': a.last_payment_date,
        'Last Payment Amount': a.last_payment_amount,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Legal Accounts');
      const ts = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      XLSX.writeFile(wb, `Legal_Attorney_Accounts_${ts}.xlsx`);
      setExportProgress(`Exported ${all.length} rows.`);
    } catch (e) {
      console.error(e);
      setExportProgress('Export failed.');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(''), 3000);
    }
  };

  const cell = (v: string | number) => (v === '' || v == null ? '—' : String(v));

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{title}</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
              <p>Loading litigation accounts…</p>
            </div>
          ) : (
            <>
              {errorMessage ? (
                <div className="error-banner" style={{ marginBottom: 12 }}>
                  <p style={{ margin: 0 }}>{errorMessage}</p>
                </div>
              ) : null}
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Total accounts:</span>
                  <span className="stat-value">{totalAccountsCount.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Showing:</span>
                  <span className="stat-value">
                    {pagination
                      ? `${((pagination.page - 1) * pagination.page_size + 1).toLocaleString()}-${Math.min(
                          pagination.page * pagination.page_size,
                          pagination.total_count
                        ).toLocaleString()} of ${pagination.total_count.toLocaleString()}`
                      : `${accounts.length.toLocaleString()} of ${totalAccountsCount.toLocaleString()}`}
                  </span>
                </div>
              </div>

              {onSearchChange && (
                <div className="search-controls">
                  <div className="search-input-group">
                    <label htmlFor="legal-search-loan">Search by loan number:</label>
                    <input
                      ref={loanNumberInputRef}
                      id="legal-search-loan"
                      type="text"
                      className="search-input"
                      placeholder="Loan number…"
                      value={searchLoanNumber}
                      onChange={(e) => onSearchChange(e.target.value, searchCustomerName)}
                      onFocus={() => {
                        lastFocusedInputRef.current = 'loan';
                      }}
                    />
                  </div>
                  <div className="search-input-group">
                    <label htmlFor="legal-search-customer">Search by customer name:</label>
                    <input
                      ref={customerNameInputRef}
                      id="legal-search-customer"
                      type="text"
                      className="search-input"
                      placeholder="Customer name…"
                      value={searchCustomerName}
                      onChange={(e) => onSearchChange(searchLoanNumber, e.target.value)}
                      onFocus={() => {
                        lastFocusedInputRef.current = 'customer';
                      }}
                    />
                  </div>
                  <div className="search-actions">
                    {isSearchPending && (
                      <div className="search-pending-indicator">
                        <span className="search-pending-text">Searching…</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="clear-search-button"
                      onClick={() => onSearchChange('', '')}
                      disabled={!searchLoanNumber && !searchCustomerName}
                    >
                      Clear search
                    </button>
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('loan_number')}
                        title="Loan number"
                      >
                        Loan # {onSortChange && getSortIcon('loan_number')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('customer_name')}
                        title="Customer name"
                      >
                        Customer {onSortChange && getSortIcon('customer_name')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('litigation_state')}
                        title="Litigation state"
                      >
                        State {onSortChange && getSortIcon('litigation_state')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('litigation_county')}
                        title="Litigation county"
                      >
                        County {onSortChange && getSortIcon('litigation_county')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('collection_officer')}
                        title="Collection officer"
                      >
                        Officer {onSortChange && getSortIcon('collection_officer')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('attorney_file_number')}
                        title="Attorney file number"
                      >
                        Atty file # {onSortChange && getSortIcon('attorney_file_number')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('case_number')}
                        title="Case number"
                      >
                        Case # {onSortChange && getSortIcon('case_number')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('case_filing_date')}
                        title="Case filing date"
                      >
                        Filed {onSortChange && getSortIcon('case_filing_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('service_date')}
                        title="Service date"
                      >
                        Service {onSortChange && getSortIcon('service_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('judgement_date')}
                        title="Judgement date"
                      >
                        Judgment {onSortChange && getSortIcon('judgement_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('garn_filing_date')}
                        title="Garnishment filing date"
                      >
                        Garn filed {onSortChange && getSortIcon('garn_filing_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('garn_service_date')}
                        title="Garnishment service date"
                      >
                        Garn served {onSortChange && getSortIcon('garn_service_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('garn_answer_date')}
                        title="Garnishment answer date (udf50)"
                      >
                        Garn answer {onSortChange && getSortIcon('garn_answer_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('last_payment_date')}
                        title="Last payment date"
                      >
                        Last pmt date {onSortChange && getSortIcon('last_payment_date')}
                      </th>
                      <th
                        className={onSortChange ? 'sortable' : ''}
                        onClick={() => onSortChange && handleSort('last_payment_amount')}
                        title="Last payment amount"
                      >
                        Last pmt amt {onSortChange && getSortIcon('last_payment_amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(accounts) || accounts.length === 0 ? (
                      <tr>
                        <td colSpan={COL_COUNT} className="no-data">
                          No accounts found
                        </td>
                      </tr>
                    ) : (
                      accounts.map((a, index) => (
                        <tr key={`${a.loan_number}-${index}`} className={index % 2 === 0 ? 'even' : 'odd'}>
                          <td className="loan-number">{cell(a.loan_number)}</td>
                          <td className="customer-name">{cell(a.customer_name)}</td>
                          <td>{cell(a.litigation_state)}</td>
                          <td>{cell(a.litigation_county)}</td>
                          <td>{cell(a.collection_officer)}</td>
                          <td>{cell(a.attorney_file_number)}</td>
                          <td>{cell(a.case_number)}</td>
                          <td>{cell(a.case_filing_date)}</td>
                          <td>{cell(a.service_date)}</td>
                          <td>{cell(a.judgement_date)}</td>
                          <td>{cell(a.garn_filing_date)}</td>
                          <td>{cell(a.garn_service_date)}</td>
                          <td>{cell(a.garn_answer_date)}</td>
                          <td>{cell(a.last_payment_date)}</td>
                          <td className="balance payoff">
                            {formatCurrency(Number(a.last_payment_amount) || 0)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination && onPageChange && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Page {pagination.page} of {pagination.total_pages} (
                      {pagination.total_count.toLocaleString()} total)
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      type="button"
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page - 1)}
                      disabled={!pagination.has_previous || loading}
                    >
                      ← Previous
                    </button>
                    <div className="page-numbers">
                      {(() => {
                        const pages: React.ReactNode[] = [];
                        const startPage = Math.max(1, pagination.page - 2);
                        const endPage = Math.min(pagination.total_pages, pagination.page + 2);
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              type="button"
                              key={i}
                              className={`page-number ${i === pagination.page ? 'active' : ''}`}
                              onClick={() => onPageChange(i)}
                              disabled={loading}
                            >
                              {i}
                            </button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    <button
                      type="button"
                      className="pagination-button"
                      onClick={() => onPageChange(pagination.page + 1)}
                      disabled={!pagination.has_next || loading}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer">
          <div className="export-section">
            <button
              type="button"
              className="export-excel-button"
              onClick={exportToExcel}
              disabled={loading || isExporting || !onExportAllData}
            >
              {isExporting ? '⏳ Exporting…' : '📊 Export to Excel'}
            </button>
            {exportProgress ? <span className="export-progress-text">{exportProgress}</span> : null}
          </div>
          <button type="button" className="close-dialog-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalAttorneyAccountsDialog;
